import { Checkbox, FormControlLabel } from '@mui/material';
import { hasEvacContent } from 'features/wildfires/utils/hasEvacContent';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormEvacZoneStatus } from 'shared/types';
import { removeHTMLTags } from 'shared/utils';

type ManualDeactivationFieldProps = {
  hasEvacZones: boolean;
};

type FormValues = {
  manualDeactivationStarted: boolean | null;
  containment: number | null;
  isFps: boolean;
  isPrescribed: boolean;
  isComplexParent: boolean;
  evacuationOrders: string;
  evacuationWarnings: string;
  evacuationAdvisories: string;
  evacuationNotes: string;
  evacZoneStatuses: FormEvacZoneStatus[];
  customOrders: boolean;
  customWarnings: boolean;
  customAdvisories: boolean;
};

/**
 * Manual DeactivationField component
 * Handles deactivation of reporter-managed incidents based on backend rules.
 *
 * The checkbox is disabled and automatically unchecked when:
 * - The incident is prescribed or a complex parent
 * - Any evacuation data exists (orders, warnings, advisories, notes, or active zones)
 * - Automatically when containment reaches 100%
 *
 * The checkbox is disabled when:
 * - Automatically for FPS incidents
 */
export const ManualDeactivationField = (
  props: ManualDeactivationFieldProps,
): JSX.Element => {
  const { hasEvacZones } = props;
  const { control, setValue, formState, watch } = useFormContext<FormValues>();
  const { t } = useTranslation();

  const [
    manualDeactivationStarted,
    containment,
    isFps,
    isPrescribed,
    isComplexParent,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationNotes,
    evacZoneStatuses,
    customOrders,
    customWarnings,
    customAdvisories,
  ] = watch([
    'manualDeactivationStarted',
    'containment',
    'isFps',
    'isPrescribed',
    'isComplexParent',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationNotes',
    'evacZoneStatuses',
    'customOrders',
    'customWarnings',
    'customAdvisories',
  ]);

  const fullContainment = containment === 100;
  const hasEvacuationOrders = hasEvacContent({
    evacContent: evacuationOrders,
    customEvac: customOrders,
    hasEvacZones,
  });
  const hasEvacuationWarnings = hasEvacContent({
    evacContent: evacuationWarnings,
    customEvac: customWarnings,
    hasEvacZones,
  });
  const hasEvacuationAdvisories = hasEvacContent({
    evacContent: evacuationAdvisories,
    customEvac: customAdvisories,
    hasEvacZones,
  });
  const hasEvacuationNotes = Boolean(removeHTMLTags(evacuationNotes ?? ''));

  const cantDeactivate = Boolean(
    fullContainment ||
      isPrescribed ||
      isComplexParent ||
      evacZoneStatuses.length ||
      hasEvacuationOrders ||
      hasEvacuationWarnings ||
      hasEvacuationAdvisories ||
      hasEvacuationNotes,
  );

  useEffect(() => {
    if (cantDeactivate && manualDeactivationStarted) {
      setValue('manualDeactivationStarted', false);
    }
  }, [cantDeactivate, manualDeactivationStarted, setValue]);

  const disabled = formState.isSubmitting || isFps || cantDeactivate;

  return (
    <Controller
      name="manualDeactivationStarted"
      control={control}
      render={({ field }): JSX.Element => {
        return (
          <FormControlLabel
            control={
              <Checkbox
                name="manualDeactivationStarted"
                checked={Boolean(field.value)}
                onChange={(event) => field.onChange(event.target.checked)}
                id="field-control-manualDeactivationStarted"
              />
            }
            label={t('createEditGeoEvent.startDeactivation')}
            disabled={disabled}
            sx={{ width: 'fit-content' }}
          />
        );
      }}
    />
  );
};
